/**
 * Modèles de PV de Délibération
 *
 * Modèle 1 — "PV Jury classique" : 3 catégories (Validé, Partiel, Non validé)
 * Modèle 2 — "PV Synthèse validés" : Liste des validés uniquement avec texte introductif
 * Le modèle "Détaillé" (existant) est dans PVPreview.tsx → buildPVHTML
 */

import type { DonneesPV, PVEtudiant, MembreJury } from '../../services/pvDeliberationApi';

export type PVModele = 'detaille' | 'jury_classique' | 'synthese_valides' | 'ministeriel' | 'synthese_bi_session';

export interface PVModeleOptions {
  salle?: string;
  juryNumero?: string;
  noteServiceNumero?: string;
  noteServiceDate?: string;
  ville?: string;
  nationaliteParDefaut?: string;
  /**
   * Mois de la session d'examens (ex: "avril", "septembre"). Indépendant de
   * la date de délibération. Si vide, le builder n'affichera pas le mois
   * (au lieu d'afficher "undefined" ou un mois calculé à tort).
   */
  moisSession?: string;
}

export const PV_MODELES: { value: PVModele; label: string; description: string }[] = [
  { value: 'detaille', label: 'Détaillé (Paysage)', description: 'Toutes les notes ECUE/UE, crédits, rang — format paysage A4' },
  { value: 'jury_classique', label: 'PV Jury Classique', description: '3 catégories : Validé / Partiel / Non validé — avec jury nommé' },
  { value: 'synthese_valides', label: 'Synthèse Validés', description: 'Liste des validés par option avec texte introductif — format portrait' },
  { value: 'ministeriel', label: 'PV Ministériel (Officiel)', description: 'Modèle officiel du Ministère de l\'Enseignement Supérieur — à déposer' },
  { value: 'synthese_bi_session', label: 'PV Synthèse Bi-Session (Ministériel)', description: 'PV global — comparaison Session Ordinaire vs Après Rattrapage (à utiliser en session=Synthèse)' },
];

// ── Helpers ──

const fN = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  return v.toFixed(2).replace('.', ',');
};

const getMention = (moy: number | null | undefined): string => {
  if (moy === null || moy === undefined) return '';
  if (moy >= 16) return 'Très Bien';
  if (moy >= 14) return 'Bien';
  if (moy >= 12) return 'Assez Bien';
  if (moy >= 10) return 'Passable';
  return '';
};

const sessionLabel = (s: string) => (s === 'rattrapage' ? 'Rattrapage' : 'Ordinaire');

const formatDateFr = (d: string) => {
  if (!d) return '...........................';
  try {
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return d; }
};

// ── En-tête commun ──

function headerHTML(logoUrl: string = '/logo.png'): string {
  return `
  <div style="display:flex;align-items:center;justify-content:center;gap:20px;margin-bottom:10px;">
    <img src="${logoUrl}" alt="Logo" style="height:65px;" onerror="this.style.display='none'" />
    <div style="text-align:center;">
      <div style="font-size:15px;font-weight:bold;letter-spacing:1px;">INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE</div>
      <div style="font-size:9px;font-style:italic;">Agrément définitif par l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
    </div>
  </div>`;
}

// ════════════════════════════════════════════════════════════════════════════
// MODÈLE 1 — PV JURY CLASSIQUE
// ════════════════════════════════════════════════════════════════════════════

export function buildPVJuryClassiqueHTML(data: DonneesPV, dateDeliberation: string, salleJury?: string): string {
  const { info_classe, annee_academique, semestre, session, etudiants, statistiques, jury } = data;

  // Trier par moyenne décroissante
  const sorted = [...etudiants].sort((a, b) => {
    const ma = a.resultat_semestre?.moyenne_semestre ?? -1;
    const mb = b.resultat_semestre?.moyenne_semestre ?? -1;
    return mb - ma;
  });

  // Catégoriser
  const valides = sorted.filter(e => e.resultat_semestre?.valide === true);
  const nbUes = data.ues_structure.length;
  const partiels = sorted.filter(e => {
    if (e.resultat_semestre?.valide) return false;
    const ueValidees = Object.values(e.ues_results).filter(u => u.validee).length;
    return ueValidees > 0 && ueValidees < nbUes;
  });
  const nonValides = sorted.filter(e => {
    if (e.resultat_semestre?.valide) return false;
    const ueValidees = Object.values(e.ues_results).filter(u => u.validee).length;
    return ueValidees === 0;
  });

  // Jury
  const president = jury.find(m => m.fonction === 'president');
  const rapporteur = jury.find(m => m.fonction === 'vice_president' || m.fonction === 'secretaire');
  const membres = jury.filter(m => m.fonction !== 'president' && m.fonction !== 'vice_president' && m.fonction !== 'secretaire');

  const juryNom = (m: MembreJury | undefined) => m ? (m.titre_academique ? `${m.titre_academique} ${m.nom_complet}` : m.nom_complet) : '......................';

  // Table builder
  const buildTable = (students: PVEtudiant[]): string => {
    if (students.length === 0) return '<p style="font-style:italic;margin:5px 0 10px 20px;">Néant</p>';
    let rows = '';
    students.forEach((e, i) => {
      const moy = e.resultat_semestre?.moyenne_semestre;
      const mention = getMention(moy != null ? parseFloat(String(moy)) : null);
      rows += `<tr>
        <td style="border:1px solid #000;padding:3px 6px;text-align:center;">${i + 1}</td>
        <td style="border:1px solid #000;padding:3px 6px;font-weight:bold;">${e.nom}</td>
        <td style="border:1px solid #000;padding:3px 6px;">${e.prenoms}</td>
        <td style="border:1px solid #000;padding:3px 6px;text-align:center;">${fN(moy != null ? parseFloat(String(moy)) : null)}</td>
        <td style="border:1px solid #000;padding:3px 6px;text-align:center;">${mention}</td>
      </tr>`;
    });
    return `<table style="width:100%;border-collapse:collapse;margin:5px 0 10px 0;">
      <thead><tr style="background:#f0f0f0;">
        <th style="border:1px solid #000;padding:3px 6px;width:30px;">N°</th>
        <th style="border:1px solid #000;padding:3px 6px;">Nom(s)</th>
        <th style="border:1px solid #000;padding:3px 6px;">Prénom(s)</th>
        <th style="border:1px solid #000;padding:3px 6px;width:70px;">Moyenne</th>
        <th style="border:1px solid #000;padding:3px 6px;width:80px;">Mention</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  };

  // Signatures du jury
  let jurySignatures = '<div style="margin-top:30px;display:flex;justify-content:space-between;flex-wrap:wrap;">';
  jury.forEach(m => {
    const nom = m.titre_academique ? `${m.titre_academique} ${m.nom_complet}` : m.nom_complet;
    jurySignatures += `<div style="text-align:center;min-width:200px;margin:10px;">
      <div style="font-size:9px;color:#666;">${m.fonction_display}</div>
      <div style="margin-top:30px;border-top:1px solid #000;padding-top:3px;font-weight:bold;">${nom}</div>
    </div>`;
  });
  jurySignatures += '</div>';

  const ueValidesPartiels = partiels.length > 0 ? Math.max(...partiels.map(e => Object.values(e.ues_results).filter(u => u.validee).length)) : 0;

  const dateStr = formatDateFr(dateDeliberation);

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>PV Jury — ${info_classe.classe_code} S${semestre}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  @media print { .no-print { display: none !important; } }
  body { font-family: 'Times New Roman', serif; font-size: 11px; color: #000; margin: 0; padding: 15px; line-height: 1.5; }
</style></head><body>

${headerHTML()}

<h2 style="text-align:center;text-decoration:underline;font-size:16px;margin:15px 0;">PROCÈS-VERBAL DE DÉLIBÉRATION</h2>

<p style="text-align:right;font-size:10px;">L'an ${new Date(dateDeliberation || Date.now()).getFullYear()} et le ${dateStr}, le Jury de délibération composé de :</p>

<div style="margin:10px 0 10px 30px;font-size:11px;">
  <p><strong>1. Président</strong> : ${juryNom(president)}</p>
  <p><strong>2. Rapporteur</strong> : ${juryNom(rapporteur)}</p>
  <p><strong>3. Membres</strong> :</p>
  <ul style="margin:0;padding-left:20px;">
    ${membres.map(m => `<li>${juryNom(m)}</li>`).join('')}
  </ul>
</div>

<p style="font-size:11px;">
  a statué sur l'option : <strong>${info_classe.option_nom} (${info_classe.option_code}) – ${info_classe.niveau} – Pôle : ${info_classe.pole_nom.toUpperCase()}</strong>
</p>

<p style="font-size:11px;">
  Sur <strong>${statistiques.effectif_total}</strong> étudiants inscrits à la session ${sessionLabel(session)} de ${new Date(dateDeliberation || Date.now()).toLocaleDateString('fr-FR', { month: 'long' })}, le jury a statué comme suit :
</p>

<h3 style="font-size:12px;margin:15px 0 5px;">Décision du Jury :</h3>

<p style="font-size:11px;font-weight:bold;margin:8px 0 3px;">A. Étudiants ayant validé le semestre ${semestre} : ${valides.length}</p>
<p style="font-size:10px;font-style:italic;">À l'issue de la session, par ordre de mérite, les étudiants ci-après :</p>
${buildTable(valides)}

<p style="font-size:11px;font-weight:bold;margin:12px 0 3px;">B. Étudiants ayant validé partiellement (UE partielles) : ${partiels.length}</p>
${buildTable(partiels)}

<p style="font-size:11px;font-weight:bold;margin:12px 0 3px;">C. Étudiants n'ayant rien validé : ${nonValides.length}</p>
${buildTable(nonValides)}

<p style="font-size:11px;margin-top:15px;">
  Soit un pourcentage de réussite <strong>${statistiques.taux_reussite.toFixed(2)} %</strong>.
</p>

<p style="font-size:10px;margin-top:5px;">
  A. Étudiants ayant validé ${nbUes} UE sur ${nbUes} : ${valides.length}<br/>
  B. Étudiants ayant validé partiellement : ${partiels.length}<br/>
  C. Étudiants n'ayant rien validé : ${nonValides.length}
</p>

${jurySignatures}

<div style="margin-top:20px;text-align:center;font-size:8px;color:#999;border-top:1px solid #ddd;padding-top:5px;">
  Institut International 2i — PV de Délibération — ${info_classe.classe_code} S${semestre} — ${annee_academique}
</div>

</body></html>`;
}


// ════════════════════════════════════════════════════════════════════════════
// MODÈLE 2 — SYNTHÈSE VALIDÉS
// ════════════════════════════════════════════════════════════════════════════

export function buildPVSyntheseValidesHTML(data: DonneesPV, dateDeliberation: string, salleJury?: string): string {
  const { info_classe, annee_academique, semestre, session, etudiants, statistiques } = data;

  // Validés triés par moyenne décroissante
  const valides = [...etudiants]
    .filter(e => e.resultat_semestre?.valide === true)
    .sort((a, b) => {
      const ma = a.resultat_semestre?.moyenne_semestre ?? -1;
      const mb = b.resultat_semestre?.moyenne_semestre ?? -1;
      return mb - ma;
    });

  let tableRows = '';
  valides.forEach((e, i) => {
    const moy = e.resultat_semestre?.moyenne_semestre;
    tableRows += `<tr>
      <td style="border:1px solid #000;padding:4px 8px;text-align:center;">${String(i + 1).padStart(2, '0')}</td>
      <td style="border:1px solid #000;padding:4px 8px;">${e.nom} ${e.prenoms}</td>
      <td style="border:1px solid #000;padding:4px 8px;text-align:center;">${fN(moy != null ? parseFloat(String(moy)) : null)}</td>
    </tr>`;
  });

  const semestreNum = semestre;
  const dateStr = formatDateFr(dateDeliberation);
  const salleStr = salleJury || 'HICHIKAWA';

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>PV Synthèse — ${info_classe.classe_code} S${semestre}</title>
<style>
  @page { size: A4 portrait; margin: 20mm; }
  @media print { .no-print { display: none !important; } }
  body { font-family: 'Times New Roman', serif; font-size: 12px; color: #000; margin: 0; padding: 20px; line-height: 1.6; }
  table { border-collapse: collapse; width: 100%; }
</style></head><body>

${headerHTML()}

<p style="text-align:right;font-size:11px;margin:10px 0;">Année Académique ${annee_academique}</p>

<h1 style="text-align:center;text-decoration:underline;font-size:18px;margin:20px 0;">PROCÈS VERBAL DE DÉLIBÉRATION</h1>

<h2 style="text-align:center;text-decoration:underline;font-size:15px;margin:10px 0;">SEMESTRE ${semestreNum}</h2>

<p style="font-size:12px;text-align:justify;margin:15px 0;">
  Conformément à la note N°................, il est réuni dans l'enceinte de 2i (salle ${salleStr}),
  le jury chargé de délibérer sur les résultats de la ${session === 'rattrapage' ? 'session de rattrapage' : 'première session d\'examen'}
  du semestre ${semestreNum}.
</p>

<p style="font-size:12px;text-align:justify;">
  À l'issue des débats, le jury a délibéré et arrêté la liste des étudiants ayant validé le semestre par ordre de mérite comme suit :
</p>

<p style="font-size:12px;font-weight:bold;margin:15px 0 5px;">
  OPTION : ${info_classe.option_nom}
</p>

<p style="text-align:center;font-weight:bold;font-size:13px;margin:10px 0;">VALIDÉ</p>

<table>
  <thead>
    <tr style="background:#f0f0f0;">
      <th style="border:1px solid #000;padding:4px 8px;width:40px;">N°</th>
      <th style="border:1px solid #000;padding:4px 8px;">NOM (S) & PRENOM(S)</th>
      <th style="border:1px solid #000;padding:4px 8px;width:80px;">MOYENNE</th>
    </tr>
  </thead>
  <tbody>
    ${tableRows || '<tr><td colspan="3" style="border:1px solid #000;padding:8px;text-align:center;font-style:italic;">Aucun étudiant validé</td></tr>'}
  </tbody>
</table>

<p style="font-size:12px;margin-top:15px;">
  Arrêtez la présente liste à <strong>${valides.length > 0 ? (valides.length === 1 ? 'Un (01)' : `${numberToWords(valides.length)} (${String(valides.length).padStart(2, '0')})`) : 'Zéro (00)'}</strong> étudiant(e)s.
</p>

<div style="margin-top:40px;text-align:center;font-size:8px;color:#999;border-top:1px solid #ddd;padding-top:5px;">
  Institut International 2i — PV de Délibération — ${info_classe.classe_code} S${semestre} — ${annee_academique}
</div>

</body></html>`;
}


// ── Helper conversion nombre → mots ──

function numberToWords(n: number): string {
  const units = ['', 'Un', 'Deux', 'Trois', 'Quatre', 'Cinq', 'Six', 'Sept', 'Huit', 'Neuf',
    'Dix', 'Onze', 'Douze', 'Treize', 'Quatorze', 'Quinze', 'Seize', 'Dix-sept', 'Dix-huit', 'Dix-neuf'];
  const tens = ['', '', 'Vingt', 'Trente', 'Quarante', 'Cinquante', 'Soixante', 'Soixante-dix', 'Quatre-vingt', 'Quatre-vingt-dix'];

  if (n < 20) return units[n];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const u = n % 10;
    if (t === 7 || t === 9) return tens[t - 1] + '-' + units[10 + u];
    return tens[t] + (u > 0 ? '-' + units[u] : '');
  }
  if (n < 1000) {
    const h = Math.floor(n / 100);
    const rest = n % 100;
    const prefix = h === 1 ? 'Cent' : units[h] + ' Cent';
    return prefix + (rest > 0 ? ' ' + numberToWords(rest) : '');
  }
  return String(n);
}

function numberToWordsLower(n: number): string {
  return numberToWords(n).toLowerCase();
}

function yearToWordsFr(year: number): string {
  if (year < 2000 || year > 2099) return String(year);
  const rest = year - 2000;
  if (rest === 0) return 'deux mil';
  return 'deux mil ' + numberToWordsLower(rest);
}

function dayMonthFr(d: Date): string {
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

function dayMonthYearFr(d: Date): string {
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Parse une date arrivant soit en ISO (`2026-06-19`), soit déjà formatée en
 * français (`19 juin 2026` / `19/06/2026`). Évite les "Invalid Date" qui
 * généraient des `NaN/NaN/NaN` et `deux mil nan` dans le PV.
 */
function parseDateFrOrISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  const trimmed = s.trim();
  // ISO 2026-06-19 ou 2026-06-19T...
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const d = new Date(trimmed.slice(0, 10) + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }
  // DD/MM/YYYY
  const slash = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const [, dd, mm, yy] = slash;
    const d = new Date(parseInt(yy), parseInt(mm) - 1, parseInt(dd));
    return isNaN(d.getTime()) ? null : d;
  }
  // "19 juin 2026" (français)
  const months: Record<string, number> = {
    janvier: 0, fevrier: 1, février: 1, mars: 2, avril: 3, mai: 4, juin: 5,
    juillet: 6, aout: 7, août: 7, septembre: 8, octobre: 9, novembre: 10, decembre: 11, décembre: 11,
  };
  const fr = trimmed.toLowerCase().match(/^(\d{1,2})\s+([a-zéûôà]+)\s+(\d{4})$/);
  if (fr) {
    const [, dd, monthName, yy] = fr;
    const m = months[monthName];
    if (m !== undefined) {
      const d = new Date(parseInt(yy), m, parseInt(dd));
      return isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════════════
// MODÈLE 3 — MINISTÉRIEL (officiel)
// ════════════════════════════════════════════════════════════════════════════

export function buildPVMinisterielHTML(data: DonneesPV, dateDeliberation: string, options: PVModeleOptions = {}): string {
  const { info_classe, semestre, session, etudiants, statistiques } = data;
  const {
    salle = '...........................',
    juryNumero = '.....',
    noteServiceNumero = '...............',
    noteServiceDate = '...........................',
    ville = 'Pointe-Noire',
    nationaliteParDefaut = 'Congolaise',
    moisSession: moisSessionOverride,
  } = options;

  // ── Dates ──
  // Le caller peut passer soit l'ISO ("2026-06-19") soit la version formatée
  // française ("19 juin 2026"). On essaye d'abord l'ISO, sinon on tombe sur
  // un parseur français manuel pour garder la rétrocompat. Un Invalid Date
  // produit auparavant des "deux mil nan" / "NaN undefined".
  const dateDelib = parseDateFrOrISO(dateDeliberation);
  const anneeLettres = dateDelib ? yearToWordsFr(dateDelib.getFullYear()) : 'deux mil .....................';
  const dateJourLettres = dateDelib ? dayMonthFr(dateDelib) : '............';
  // Le mois de la session d'examens est INDÉPENDANT de la date de
  // délibération (on peut délibérer en juin alors que les examens étaient
  // en mai). Toujours passer le mois via `options.moisSession`. Si vide on
  // n'affiche rien plutôt qu'un mois inventé.
  const moisSession = (moisSessionOverride || '').trim();
  const sessionLabelMin = session === 'rattrapage' ? 'RATTRAPAGE' : 'ORDINAIRE';
  const sessionMot = session === 'rattrapage' ? 'rattrapage' : 'ordinaire';
  const sessionRangMot = session === 'rattrapage' ? 'deuxième' : 'première';
  const semestreParite = semestre % 2 === 1 ? 'IMPAIR' : 'PAIR';

  // ── Cycle (Licence / Master / Doctorat) ──
  const cycleLower = (info_classe.cycle || 'Licence').toLowerCase();
  const cycleAffiche = cycleLower.includes('master') ? 'Master'
                    : cycleLower.includes('doctorat') ? 'Doctorat'
                    : 'Licence';

  // ── Parcours / filière / mention ──
  const parcoursTxt = info_classe.parcours_nom || info_classe.option_nom || '...........................';

  // ── Stats : SOURCE DE VÉRITÉ = les copies scannées ──
  //
  // Le backend calcule les participants à partir de `CopieExamen` :
  //   - Une copie scannée + associée à l'étudiant = il s'est présenté à l'examen
  //     (filtre type_examen='NORMAL' pour ordinaire, 'RATTRAPAGE' pour rattrapage)
  //   - Aucune copie scannée = absent total pour cette session
  //
  // Ces champs viennent de `statistiques.nombre_participants` /
  // `nombre_absents`. Fallback silencieux sur un calcul local (moyenne_ecue)
  // pour la rétrocompat avec les anciens payloads.
  const effectif = statistiques?.effectif_total || etudiants.length;
  const presents = statistiques?.nombre_participants ?? etudiants.filter((e) =>
    Object.values(e.notes || {}).some((n) => n && n.moyenne_ecue !== null),
  ).length;
  const absents = statistiques?.nombre_absents ?? Math.max(0, effectif - presents);
  const admis = statistiques?.nombre_admis ?? etudiants.filter((e) => e.resultat_semestre?.valide === true).length;
  const ajournes = statistiques?.nombre_ajournes ?? Math.max(0, presents - admis);
  const pct = (n: number) =>
    effectif > 0
      ? ((n / effectif) * 100).toFixed(2).replace('.', ',') + ' %'
      : '0,00 %';

  // ── Validés triés par moyenne décroissante ──
  // Depuis la refonte session-aware du backend (2026-08-15), `valide=true`
  // reflète maintenant la validation POUR LA SESSION CHOISIE :
  //   - session=ordinaire  → validés à la session ordinaire uniquement
  //   - session=rattrapage → validés grâce au rattrapage (ne liste QUE les
  //                           rattrapeurs — pas le cumul SO+SR)
  // Pour un PV ministériel GLOBAL (SO + SR cumulés), il faut utiliser le
  // modèle "Synthèse Bi-Session" avec session=synthese.
  const valides = [...etudiants]
    .filter((e) => e.resultat_semestre?.valide === true)
    .sort((a, b) => {
      const ma = a.resultat_semestre?.moyenne_semestre ?? -1;
      const mb = b.resultat_semestre?.moyenne_semestre ?? -1;
      return mb - ma;
    });

  // ── Format date naissance ──
  // Le backend renvoie déjà du DD/MM/YYYY. On accepte aussi l'ISO et on rend
  // "—" pour les valeurs vides ou invalides (au lieu de "NaN/NaN/NaN").
  const fmtNaissance = (s: string | null | undefined): string => {
    if (!s || typeof s !== 'string') return '—';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const [y, m, d] = s.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    }
    return '—';
  };

  // ── Sexe affichage ──
  const fmtSexe = (s: string): string => {
    const v = (s || '').trim().toUpperCase();
    if (v === 'M' || v === 'MASCULIN' || v === 'H') return 'M';
    if (v === 'F' || v === 'FEMININ' || v === 'FÉMININ') return 'F';
    return v ? v.charAt(0) : '—';
  };

  // ── Table validés ──
  let tableRows = '';
  valides.forEach((e, i) => {
    const moy = e.resultat_semestre?.moyenne_semestre;
    const mention = getMention(moy != null ? parseFloat(String(moy)) : null);
    tableRows += `<tr>
      <td style="border:1px solid #000;padding:4px 6px;text-align:center;">${i + 1}</td>
      <td style="border:1px solid #000;padding:4px 6px;font-weight:600;">${e.nom} ${e.prenoms}</td>
      <td style="border:1px solid #000;padding:4px 6px;text-align:center;white-space:nowrap;">${fmtNaissance(e.date_naissance)}</td>
      <td style="border:1px solid #000;padding:4px 6px;text-align:center;">${fmtSexe(e.sexe)}</td>
      <td style="border:1px solid #000;padding:4px 6px;text-align:center;">${nationaliteParDefaut}</td>
      <td style="border:1px solid #000;padding:4px 6px;text-align:center;font-weight:600;">${fN(moy != null ? parseFloat(String(moy)) : null)}</td>
      <td style="border:1px solid #000;padding:4px 6px;text-align:center;">${mention}</td>
    </tr>`;
  });

  // ── "Arrêté la présente liste à X admis" en lettres ──
  const nbValides = valides.length;
  const nbValidesLettres = nbValides === 0 ? 'Zéro' : numberToWords(nbValides);
  const arreteText = `${nbValidesLettres} (${String(nbValides).padStart(2, '0')})`;

  // ── Date "Fait à ..., le ..." ──
  const faitDateStr = dateDelib ? dayMonthYearFr(dateDelib) : '.................................';

  // ── Niveau / Option / Classe ──
  // Affiché en bandeau juste après le titre pour identifier immédiatement la
  // promotion concernée par le PV (ex: L1 — Comptabilité Générale — L1CGE).
  const niveauLabel = info_classe.niveau || '';
  const optionNomComplet = info_classe.option_nom || '';
  const classeCodeAff = info_classe.classe_code || '';

  const isMois = !!moisSession;

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>PV Ministériel — ${info_classe.classe_code} S${semestre} ${sessionLabelMin}</title>
<style>
  @page { size: A4 portrait; margin: 13mm 16mm 13mm 16mm; }
  @media print { .no-print { display: none !important; } }
  body { font-family: 'Times New Roman', serif; font-size: 12px; color: #000; margin: 0; padding: 0; line-height: 1.55; }
  .entete { display: table; width: 100%; border-collapse: collapse; border: 1px solid #000; margin-bottom: 6px; }
  .entete-cell { display: table-cell; vertical-align: middle; padding: 6px 10px; }
  .entete .col-gauche { width: 60%; font-size: 9.5px; line-height: 1.35; border-right: 1px solid #000; }
  .entete .col-droite { width: 40%; text-align: center; }
  .entete .col-gauche .ligne-titre { font-weight: bold; }
  .entete .col-gauche .ligne-sep { color: #444; letter-spacing: 1px; }
  .entete .institut { font-weight: bold; font-size: 11px; margin-top: 4px; text-transform: uppercase; }
  .entete .agrement { font-size: 8.5px; font-style: italic; }
  .bandeau-classe { border: 1px solid #000; background: #f6f6f6; padding: 6px 10px; text-align: center; font-size: 12px; margin-bottom: 8px; }
  .bandeau-classe strong { font-size: 13px; }
  .titre { text-align: center; font-weight: bold; font-size: 13px; text-transform: uppercase; margin: 10px 0 8px; text-decoration: underline; line-height: 1.45; }
  .para { text-align: justify; margin: 8px 0; }
  .stats-table { border-collapse: collapse; margin: 8px auto; width: 65%; }
  .stats-table th, .stats-table td { border: 1px solid #000; padding: 5px 10px; font-size: 11px; }
  .stats-table th { background: #ececec; text-align: center; font-weight: bold; }
  .stats-table td:first-child { font-weight: 600; }
  .stats-table td:nth-child(2), .stats-table td:nth-child(3) { text-align: center; }
  .stats-table tbody tr:nth-child(even) td { background: #fafafa; }
  .valides-table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 11px; }
  .valides-table th { background: #ececec; border: 1px solid #000; padding: 5px; text-align: center; font-weight: bold; }
  .valides-table tbody tr:nth-child(even) td { background: #fafafa; }
  .signatures { margin-top: 28px; width: 100%; border-collapse: collapse; }
  .signatures td { vertical-align: top; padding: 0 10px; width: 50%; }
  .sig-block { margin-bottom: 48px; }
  .sig-label { font-weight: 600; margin-bottom: 48px; }
  .fait-line { text-align: right; margin-top: 16px; margin-bottom: 8px; font-style: italic; }
  .arrete-line { margin: 12px 0; font-weight: 500; }
  .underline-input { border-bottom: 1px dotted #666; padding: 0 4px; min-width: 30px; display: inline-block; }
</style></head><body>

<!-- En-tête institutionnel (deux colonnes avec séparateur) -->
<div class="entete">
  <div class="entete-cell col-gauche">
    <div class="ligne-titre">RÉPUBLIQUE DU CONGO</div>
    <div style="font-style:italic;">Unité * Travail * Progrès</div>
    <div class="ligne-sep">— — — — —</div>
    <div>Ministère de l'Enseignement Supérieur,</div>
    <div>de la Recherche Scientifique</div>
    <div>et de l'Innovation Technologique</div>
    <div class="ligne-sep">— — — — —</div>
    <div class="institut">Institut International Polytechnique &amp; Commerce 2i</div>
    <div class="agrement">Agrément définitif S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
  </div>
  <div class="entete-cell col-droite">
    <img src="/logo.png" alt="Logo Institut 2i" style="height:90px;" onerror="this.style.display='none'" />
  </div>
</div>

<div class="titre">
  Procès-Verbal de Délibération des Résultats de la Session<br/>
  ${sessionLabelMin} du Semestre ${semestreParite} (S${semestre})
</div>

<!-- Bandeau identifiant la promotion (niveau + option + classe) -->
<div class="bandeau-classe">
  <strong>${cycleAffiche} ${niveauLabel}</strong>
  ${optionNomComplet ? `&nbsp;&mdash;&nbsp;Option&nbsp;: <strong>${optionNomComplet}</strong>` : ''}
  ${classeCodeAff ? `&nbsp;&mdash;&nbsp;Classe&nbsp;: <strong>${classeCodeAff}</strong>` : ''}
  &nbsp;&mdash;&nbsp;Année académique&nbsp;: <strong>${data.annee_academique || ''}</strong>
</div>

<p class="para">
  L'an <strong>${anneeLettres}</strong>, le <strong>${dateJourLettres}</strong>,
  le jury n° <span class="underline-input">${juryNumero}</span> de <strong>${cycleAffiche} ${niveauLabel}</strong>
  du parcours / filière / mention <strong>${parcoursTxt}</strong>${optionNomComplet && optionNomComplet !== parcoursTxt ? `, option <strong>${optionNomComplet}</strong>` : ''},
  nommé par la note de service n°<span class="underline-input">${noteServiceNumero}</span>
  du <span class="underline-input">${noteServiceDate}</span>,
  s'est réuni dans la salle de <strong>${salle}</strong>
  pour délibérer les résultats des épreuves écrites de la session
  ${isMois ? `de <strong>${moisSession}</strong>` : ''} du semestre <strong>${semestre}</strong>.
</p>

<p style="margin:10px 0 4px;">Les résultats se présentent comme suit :</p>

<table class="stats-table">
  <thead>
    <tr><th>Nature</th><th>Effectifs</th><th>Pourcentage (%)</th></tr>
  </thead>
  <tbody>
    <tr><td>Inscrits</td><td>${effectif}</td><td>100,00 %</td></tr>
    <tr><td>Présents</td><td>${presents}</td><td>${pct(presents)}</td></tr>
    <tr><td>Absents</td><td>${absents}</td><td>${pct(absents)}</td></tr>
    <tr><td>Admis</td><td>${admis}</td><td>${pct(admis)}</td></tr>
    <tr><td>Ajournés</td><td>${ajournes}</td><td>${pct(ajournes)}</td></tr>
  </tbody>
</table>

<p class="para">
  Ont validé le semestre <strong>${semestre}</strong>, <strong>${sessionMot}</strong>
  à l'issue de la <strong>${sessionRangMot}</strong> session,
  les étudiants dont les noms et prénoms suivent :
</p>

<table class="valides-table">
  <thead>
    <tr>
      <th style="width:30px;">N°</th>
      <th>Noms et Prénoms</th>
      <th style="width:85px;">Date de naissance</th>
      <th style="width:35px;">Sexe</th>
      <th style="width:90px;">Nationalité</th>
      <th style="width:65px;">Moyenne</th>
      <th style="width:90px;">Mention</th>
    </tr>
  </thead>
  <tbody>
    ${tableRows || '<tr><td colspan="7" style="border:1px solid #000;padding:12px;text-align:center;font-style:italic;">Aucun étudiant validé</td></tr>'}
  </tbody>
</table>

<p class="arrete-line">
  Arrêté la présente liste à <strong>${arreteText}</strong> admis.
</p>

<p class="fait-line">
  Fait à <strong>${ville}</strong>, le <strong>${faitDateStr}</strong>
</p>

<table class="signatures">
  <tr>
    <td>
      <div class="sig-block">
        <div class="sig-label">Les Rapporteurs</div>
      </div>
      <div class="sig-block">
        <div class="sig-label">Membres</div>
      </div>
    </td>
    <td>
      <div class="sig-block">
        <div class="sig-label">Le Président du jury,</div>
      </div>
      <div class="sig-block">
        <div class="sig-label">Le Président général des jurys des Licences/Masters</div>
      </div>
    </td>
  </tr>
</table>

</body></html>`;
}

// ════════════════════════════════════════════════════════════════════════════
// MODÈLE 4 — SYNTHÈSE BI-SESSION (ministériel global : SO + Rattrapage)
// ════════════════════════════════════════════════════════════════════════════

/**
 * PV Synthèse Bi-Session : les deux sessions présentées côte-à-côte pour
 * chaque étudiant de la classe.
 *
 * Utilise les données renvoyées quand `session=synthese` :
 *   - moyenne_semestre_ordinaire + decision_ordinaire (état à la fin de SO)
 *   - moyenne_semestre_finale + decision_finale (état après rattrapage)
 *   - a_fait_rattrapage (indicateur visuel)
 *
 * Statistiques bi-session : participants SO / participants Ratt / admis SO /
 * admis grâce au rattrapage / total admis.
 *
 * Format A4 paysage pour tenir la table en largeur.
 */
export function buildPVSyntheseBiSessionHTML(
  data: DonneesPV, dateDeliberation: string, options: PVModeleOptions = {},
): string {
  const { info_classe, semestre, etudiants, statistiques } = data;
  const {
    ville = 'Pointe-Noire',
    nationaliteParDefaut = 'Congolaise',
  } = options;

  const dateDelib = parseDateFrOrISO(dateDeliberation);
  const faitDateStr = dateDelib ? dayMonthYearFr(dateDelib) : '.................................';
  const semestreParite = semestre % 2 === 1 ? 'IMPAIR' : 'PAIR';
  const cycleLower = (info_classe.cycle || 'Licence').toLowerCase();
  const cycleAffiche = cycleLower.includes('master') ? 'Master'
                    : cycleLower.includes('doctorat') ? 'Doctorat'
                    : 'Licence';
  const niveauLabel = info_classe.niveau || '';
  const optionNomComplet = info_classe.option_nom || '';
  const classeCodeAff = info_classe.classe_code || '';

  const effectif = statistiques?.effectif_total || etudiants.length;
  const partSO = statistiques?.nombre_participants_ordinaire ?? 0;
  const partSR = statistiques?.nombre_participants_rattrapage ?? 0;
  const admisSO = statistiques?.nombre_admis_ordinaire ?? 0;
  const admisSR = statistiques?.nombre_admis_rattrapage ?? 0;
  const admisTotal = statistiques?.nombre_admis ?? (admisSO + admisSR);
  const ajournesFinal = statistiques?.nombre_ajournes ?? Math.max(0, effectif - admisTotal);
  const pct = (n: number) => effectif > 0
    ? ((n / effectif) * 100).toFixed(2).replace('.', ',') + ' %'
    : '0,00 %';

  const fmtNaissance = (s: string | null | undefined): string => {
    if (!s || typeof s !== 'string') return '—';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const [y, m, d] = s.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    }
    return '—';
  };
  const fmtSexe = (s: string): string => {
    const v = (s || '').trim().toUpperCase();
    if (v === 'M' || v === 'MASCULIN' || v === 'H') return 'M';
    if (v === 'F' || v === 'FEMININ' || v === 'FÉMININ') return 'F';
    return v ? v.charAt(0) : '—';
  };
  const decisionCell = (dec?: string): string => {
    const d = (dec || '').toLowerCase();
    if (d.includes('admis')) return `<span style="color:#065f46;font-weight:600;">Admis</span>`;
    if (d.includes('ajour')) return `<span style="color:#991b1b;">Ajourné</span>`;
    return dec || '—';
  };

  // Tri : ligne par ligne, admis en tête puis ajournés, par moyenne finale desc
  const rows = [...etudiants].sort((a, b) => {
    const va = a.resultat_semestre?.valide_finale ? 1 : 0;
    const vb = b.resultat_semestre?.valide_finale ? 1 : 0;
    if (va !== vb) return vb - va;
    const ma = a.resultat_semestre?.moyenne_semestre_finale
      ?? a.resultat_semestre?.moyenne_semestre ?? -1;
    const mb = b.resultat_semestre?.moyenne_semestre_finale
      ?? b.resultat_semestre?.moyenne_semestre ?? -1;
    return mb - ma;
  });

  let tableRows = '';
  rows.forEach((e, i) => {
    const rs = e.resultat_semestre;
    const moySO = rs?.moyenne_semestre_ordinaire ?? null;
    const moyFin = rs?.moyenne_semestre_finale ?? rs?.moyenne_semestre ?? null;
    const decSO = rs?.decision_ordinaire || (rs?.valide_ordinaire ? 'Admis' : 'Ajourné');
    const decFin = rs?.decision_finale || rs?.decision || (rs?.valide_finale ? 'Admis' : 'Ajourné');
    const source = rs?.valide_ordinaire
      ? '<span style="color:#065f46;font-weight:600;">Session ordinaire</span>'
      : (rs?.valide_finale
          ? '<span style="color:#92400e;font-weight:600;">Rattrapage</span>'
          : '<span style="color:#6b7280;">—</span>');
    const rattBadge = e.a_fait_rattrapage
      ? ' <span style="background:#fef3c7;color:#92400e;padding:1px 5px;border-radius:8px;font-size:9px;">R</span>'
      : '';
    const mention = getMention(moyFin);
    tableRows += `<tr>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;">${i + 1}</td>
      <td style="border:1px solid #000;padding:3px 5px;font-weight:600;">${e.nom} ${e.prenoms}${rattBadge}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;white-space:nowrap;">${fmtNaissance(e.date_naissance)}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;">${fmtSexe(e.sexe)}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;">${nationaliteParDefaut}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;font-weight:600;">${fN(moySO)}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;">${decisionCell(decSO)}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;font-weight:600;">${fN(moyFin)}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;">${decisionCell(decFin)}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;">${mention || '—'}</td>
      <td style="border:1px solid #000;padding:3px 5px;text-align:center;font-size:10px;">${source}</td>
    </tr>`;
  });

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>PV Synthèse Bi-Session — ${info_classe.classe_code} S${semestre}</title>
<style>
  @page { size: A4 landscape; margin: 10mm 12mm; }
  @media print { .no-print { display: none !important; } }
  body { font-family: 'Times New Roman', serif; font-size: 11px; color: #000; margin: 0; padding: 0; line-height: 1.4; }
  .entete { display: table; width: 100%; border-collapse: collapse; border: 1px solid #000; margin-bottom: 6px; }
  .entete-cell { display: table-cell; vertical-align: middle; padding: 5px 8px; }
  .entete .col-gauche { width: 65%; font-size: 9px; line-height: 1.3; border-right: 1px solid #000; }
  .entete .col-droite { width: 35%; text-align: center; }
  .entete .institut { font-weight: bold; font-size: 10.5px; margin-top: 3px; text-transform: uppercase; }
  .entete .agrement { font-size: 8px; font-style: italic; }
  .bandeau-classe { border: 1px solid #000; background: #f6f6f6; padding: 5px 8px; text-align: center; font-size: 11px; margin-bottom: 6px; }
  .titre { text-align: center; font-weight: bold; font-size: 13px; text-transform: uppercase; margin: 8px 0 6px; text-decoration: underline; }
  .sous-titre { text-align: center; font-size: 10.5px; margin-bottom: 6px; color: #333; }
  .stats-table { border-collapse: collapse; margin: 6px auto; width: 88%; font-size: 10px; }
  .stats-table th, .stats-table td { border: 1px solid #000; padding: 4px 8px; text-align: center; }
  .stats-table th { background: #ececec; font-weight: bold; }
  .stats-table td.label { text-align: left; font-weight: 600; background: #f9fafb; }
  .valides-table { border-collapse: collapse; width: 100%; margin: 6px 0; font-size: 10.5px; }
  .valides-table th { background: #ececec; border: 1px solid #000; padding: 4px 3px; text-align: center; font-weight: bold; }
  .valides-table th.grp-so { background: #dbeafe; }
  .valides-table th.grp-fin { background: #dcfce7; }
  .valides-table tbody tr:nth-child(even) td { background: #fafafa; }
  .arrete-line { margin: 10px 0; font-weight: 500; }
  .fait-line { text-align: right; margin-top: 12px; margin-bottom: 8px; font-style: italic; }
  .signatures { margin-top: 22px; width: 100%; border-collapse: collapse; }
  .signatures td { vertical-align: top; padding: 0 10px; width: 50%; }
  .sig-block { margin-bottom: 38px; }
  .sig-label { font-weight: 600; margin-bottom: 38px; }
  .legende { font-size: 9px; color: #444; margin: 4px 0 10px; text-align: center; }
</style></head><body>

<div class="entete">
  <div class="entete-cell col-gauche">
    <div style="font-weight:bold;">RÉPUBLIQUE DU CONGO</div>
    <div style="font-style:italic;">Unité * Travail * Progrès</div>
    <div style="color:#444;letter-spacing:1px;">— — — — —</div>
    <div>Ministère de l'Enseignement Supérieur, de la Recherche Scientifique</div>
    <div>et de l'Innovation Technologique</div>
    <div style="color:#444;letter-spacing:1px;">— — — — —</div>
    <div class="institut">Institut International Polytechnique &amp; Commerce 2i</div>
    <div class="agrement">Agrément définitif S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
  </div>
  <div class="entete-cell col-droite">
    <img src="/logo.png" alt="Logo Institut 2i" style="height:75px;" onerror="this.style.display='none'" />
  </div>
</div>

<div class="titre">
  Procès-Verbal Synthèse Bi-Session — Semestre ${semestreParite} (S${semestre})
</div>
<div class="sous-titre">
  Comparaison Session Ordinaire · Session de Rattrapage
</div>

<div class="bandeau-classe">
  <strong>${cycleAffiche} ${niveauLabel}</strong>
  ${optionNomComplet ? `&nbsp;&mdash;&nbsp;Option&nbsp;: <strong>${optionNomComplet}</strong>` : ''}
  ${classeCodeAff ? `&nbsp;&mdash;&nbsp;Classe&nbsp;: <strong>${classeCodeAff}</strong>` : ''}
  &nbsp;&mdash;&nbsp;Année académique&nbsp;: <strong>${data.annee_academique || ''}</strong>
</div>

<table class="stats-table">
  <thead>
    <tr>
      <th>Indicateur</th><th>Session Ordinaire</th><th>Session Rattrapage</th><th>Total</th><th>%</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td class="label">Inscrits</td>
      <td colspan="3">${effectif}</td>
      <td>100,00 %</td>
    </tr>
    <tr>
      <td class="label">Présents</td>
      <td>${partSO}</td>
      <td>${partSR}</td>
      <td>—</td>
      <td>SO : ${pct(partSO)}</td>
    </tr>
    <tr>
      <td class="label">Admis</td>
      <td>${admisSO}</td>
      <td>${admisSR}</td>
      <td><strong>${admisTotal}</strong></td>
      <td>Global : ${pct(admisTotal)}</td>
    </tr>
    <tr>
      <td class="label">Ajournés (final)</td>
      <td colspan="3">${ajournesFinal}</td>
      <td>${pct(ajournesFinal)}</td>
    </tr>
  </tbody>
</table>

<p class="legende">
  <strong>Lecture</strong> : "Admis Session Ordinaire" = validés dès la 1<sup>ère</sup> session.
  "Admis Session Rattrapage" = validés grâce au rattrapage (ajournés en SO puis admis après SR).
  <strong>Total Admis</strong> = SO + Rattrapage. <span style="background:#fef3c7;padding:1px 5px;border-radius:8px;">R</span> = étudiant ayant composé au rattrapage.
</p>

<table class="valides-table">
  <thead>
    <tr>
      <th rowspan="2" style="width:26px;">N°</th>
      <th rowspan="2">Noms et Prénoms</th>
      <th rowspan="2" style="width:78px;">Date de naissance</th>
      <th rowspan="2" style="width:30px;">Sexe</th>
      <th rowspan="2" style="width:78px;">Nationalité</th>
      <th class="grp-so" colspan="2">Session Ordinaire</th>
      <th class="grp-fin" colspan="2">Après Rattrapage</th>
      <th rowspan="2" style="width:70px;">Mention</th>
      <th rowspan="2" style="width:70px;">Validé par</th>
    </tr>
    <tr>
      <th class="grp-so" style="width:55px;">Moy.</th>
      <th class="grp-so" style="width:65px;">Décision</th>
      <th class="grp-fin" style="width:55px;">Moy.</th>
      <th class="grp-fin" style="width:65px;">Décision</th>
    </tr>
  </thead>
  <tbody>
    ${tableRows || '<tr><td colspan="11" style="border:1px solid #000;padding:12px;text-align:center;font-style:italic;">Aucun étudiant</td></tr>'}
  </tbody>
</table>

<p class="arrete-line">
  Arrêté la présente synthèse à <strong>${admisTotal}</strong> admis
  (dont <strong>${admisSO}</strong> en session ordinaire et
  <strong>${admisSR}</strong> après rattrapage) et
  <strong>${ajournesFinal}</strong> ajournés.
</p>

<p class="fait-line">
  Fait à <strong>${ville}</strong>, le <strong>${faitDateStr}</strong>
</p>

<table class="signatures">
  <tr>
    <td>
      <div class="sig-block"><div class="sig-label">Les Rapporteurs</div></div>
      <div class="sig-block"><div class="sig-label">Membres</div></div>
    </td>
    <td>
      <div class="sig-block"><div class="sig-label">Le Président du jury,</div></div>
      <div class="sig-block"><div class="sig-label">Le Président général des jurys des Licences/Masters</div></div>
    </td>
  </tr>
</table>

</body></html>`;
}
